import { type ReactNode, useState } from "react";

import AskModal from "@/components/AskModal";
import SignedInBar from "@/components/SignedInBar";

interface Props {
  title: string;
  subtitle?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}

/**
 * The frame for every screen about one body of water: the top bar with the
 * screen links and the pool switcher, the page title, and the Ask button.
 *
 * The links used to live in a bar along the bottom, on the theory that the
 * bottom third of a phone is the reachable part. Once the top bar carried
 * the pool switcher, the theme and the account, two bars were one too many;
 * the links moved up and fold behind a menu button on a phone. The More
 * screen went the same way: a page that was only a list of links is a menu.
 */
export default function AppShell({ title, subtitle, action, children }: Props) {
  const [asking, setAsking] = useState(false);

  return (
    <div className="min-h-dvh pb-[calc(5rem+env(safe-area-inset-bottom))]">
      <SignedInBar />

      <main className="mx-auto max-w-3xl px-4 py-5">
        <div className="mb-4 flex items-end justify-between gap-3">
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-semibold tracking-tight">{title}</h1>
            {subtitle && <p className="truncate text-sm text-(--color-ink-muted)">{subtitle}</p>}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
        {children}
      </main>

      {/* Reachable from every screen — questions arrive wherever you are. */}
      <button
        type="button"
        onClick={() => setAsking(true)}
        className="no-print fixed right-4 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-20 rounded-full bg-(--color-brand) px-4 py-3 text-sm font-semibold text-(--color-on-brand) shadow-lg"
      >
        Ask
      </button>

      {asking && <AskModal onClose={() => setAsking(false)} />}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="card px-5 py-10 text-center">
      <p className="font-medium">{title}</p>
      <p className="mx-auto mt-1 max-w-xs text-sm text-(--color-ink-muted)">{body}</p>
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <p role="alert" className="text-sm text-(--color-status-high)">
      {message}
    </p>
  );
}
